import React, { useState, useRef, useCallback, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    Divider,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Draggable from "react-draggable";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { openExternal } from "../../../utils/openExternal";

const SectionAccordion = ({
    title,
    emoji,
    children,
    expanded,
    onChange,
}) => {
    const theme = useTheme();

    return (
        <Accordion
            disableGutters
            elevation={0}
            expanded={expanded}
            onChange={onChange}
            sx={{
                backgroundColor: "transparent",
                "&:before": { display: "none" },
                borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
            }}
        >
            <AccordionSummary
                expandIcon={
                    <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                }
                sx={{
                    px: 0,
                    py: 0.5,
                    minHeight: "unset",
                    "& .MuiAccordionSummary-content": { my: 0 },
                }}
            >
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                    {emoji} {title}
                </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 0, pt: 0, pb: 1 }}>
                {children}
            </AccordionDetails>
        </Accordion>
    );
};

const LeadContextPanel = ({ leadContext, drawerWidth, appBarHeight }) => {
    const theme = useTheme();
    const nodeRef = useRef(null);

    const panelWidth = 320;
    const panelHeight = 520;

    const [position, setPosition] = useState({
        x: drawerWidth + 10,
        y: appBarHeight + 480,
    });

    const [expandedPanel, setExpandedPanel] = useState(null);

    const handleDrag = useCallback((e, data) => {
        setPosition({ x: data.x, y: data.y });
    }, []);

    const handleAccordionChange = useCallback(
        (panel) => (event, isExpanded) => {
            setExpandedPanel(isExpanded ? panel : null);
        },
        [],
    );

    useEffect(() => {
        const handleResize = () => {
            setPosition((prev) => ({
                x: Math.max(
                    Math.min(prev.x, window.innerWidth - panelWidth),
                    drawerWidth + 10,
                ),
                y: Math.max(
                    Math.min(prev.y, window.innerHeight - panelHeight),
                    appBarHeight + 50,
                ),
            }));
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, [drawerWidth, appBarHeight]);

    useEffect(() => {
        setExpandedPanel(null);
    }, [leadContext]);

    if (!leadContext) return null;

    const { strikeCard } = leadContext;

    return (
        <Draggable
            nodeRef={nodeRef}
            handle=".lead-handle"
            position={position}
            onDrag={handleDrag}
            bounds={{
                left: drawerWidth + 10,
                top: appBarHeight + 50,
                right: window.innerWidth - panelWidth,
                bottom: window.innerHeight - panelHeight,
            }}
        >
            <Paper
                ref={nodeRef}
                elevation={6}
                sx={{
                    position: "fixed",
                    width: panelWidth,
                    maxHeight: panelHeight,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    backdropFilter: "blur(16px)",
                    overflow: "hidden",
                    zIndex: theme.zIndex.drawer + 2,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    className="lead-handle"
                    sx={{
                        backgroundColor: theme.palette.teamColors.peach,
                        color: theme.palette.primary.contrastText,
                        px: 2,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "move",
                        userSelect: "none",
                        flexShrink: 0,
                    }}
                >
                    <DragIndicatorIcon fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                        Lead Info
                    </Typography>

                    <Chip
                        label={leadContext.status || "Unknown"}
                        size="small"
                        sx={{
                            backgroundColor: alpha(theme.palette.background.paper, 0.3),
                            color: theme.palette.common.white,
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            height: 20,
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                        "&::-webkit-scrollbar": { width: 6 },
                        "&::-webkit-scrollbar-track": {
                            background: alpha(theme.palette.background.default, 0.25),
                            borderRadius: 99,
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: alpha(theme.palette.common.white, 0.18),
                            borderRadius: 99,
                        },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <BusinessIcon
                            sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
                        />
                        <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                                {leadContext.companyName}
                            </Typography>

                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                {leadContext.industry && (
                                    <Typography variant="caption" color="text.secondary">
                                        {leadContext.industry}
                                    </Typography>
                                )}

                                {leadContext.employees && (
                                    <Typography variant="caption" color="text.secondary">
                                        · {leadContext.employees} employees
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {leadContext.contactName && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PersonIcon
                                sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
                            />
                            <Box>
                                <Typography variant="body2">
                                    {leadContext.contactName}
                                </Typography>

                                {leadContext.contactTitle && (
                                    <Typography variant="caption" color="text.secondary">
                                        {leadContext.contactTitle}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    {leadContext.location && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LocationOnIcon
                                sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
                            />
                            <Typography variant="body2">{leadContext.location}</Typography>
                        </Box>
                    )}

                    {leadContext.url && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LanguageIcon
                                sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
                            />
                            <Typography
                                variant="body2"
                                component="a"
                                href={leadContext.url}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (/^https?:\/\//i.test(leadContext.url)) {
                                        openExternal(leadContext.url);
                                    }
                                }}
                                sx={{
                                    cursor: "pointer",
                                    color: "secondary.main",
                                    textDecoration: "none",
                                    "&:hover": { textDecoration: "underline" },
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {leadContext.url.replace(/^https?:\/\//, "")}
                            </Typography>
                        </Box>
                    )}

                    {strikeCard && (
                        <>
                            <Divider
                                sx={{
                                    borderColor: alpha(theme.palette.common.white, 0.1),
                                    my: 0.5,
                                }}
                            />

                            {strikeCard.repNote && (
                                <Box
                                    sx={{
                                        p: 1,
                                        borderRadius: 1,
                                        backgroundColor: alpha(theme.palette.warning.main, 0.12),
                                        border: `1px solid ${alpha(
                                            theme.palette.warning.main,
                                            0.25,
                                        )}`,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="warning.light"
                                        fontWeight={600}
                                    >
                                        🧠 Rep Note
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        display="block"
                                        sx={{ mt: 0.25 }}
                                    >
                                        {strikeCard.repNote}
                                    </Typography>
                                </Box>
                            )}

                            {strikeCard.whatTheyDo && (
                                <SectionAccordion
                                    title="What They Do"
                                    emoji="🏢"
                                    expanded={expandedPanel === "whatTheyDo"}
                                    onChange={handleAccordionChange("whatTheyDo")}
                                >
                                    <Typography variant="caption" sx={{ lineHeight: 1.6 }}>
                                        {strikeCard.whatTheyDo}
                                    </Typography>
                                </SectionAccordion>
                            )}

                            {strikeCard.whyPeopleCall?.length > 0 && (
                                <SectionAccordion
                                    title="Why People Call"
                                    emoji="📞"
                                    expanded={expandedPanel === "whyPeopleCall"}
                                    onChange={handleAccordionChange("whyPeopleCall")}
                                >
                                    {strikeCard.whyPeopleCall.map((item, i) => (
                                        <Typography
                                            key={i}
                                            variant="caption"
                                            display="block"
                                            sx={{ lineHeight: 1.6 }}
                                        >
                                            • {item}
                                        </Typography>
                                    ))}
                                </SectionAccordion>
                            )}

                            {strikeCard.whatMatters?.length > 0 && (
                                <SectionAccordion
                                    title="What Matters"
                                    emoji="🎯"
                                    expanded={expandedPanel === "whatMatters"}
                                    onChange={handleAccordionChange("whatMatters")}
                                >
                                    {strikeCard.whatMatters.map((item, i) => (
                                        <Typography
                                            key={i}
                                            variant="caption"
                                            display="block"
                                            sx={{ lineHeight: 1.6 }}
                                        >
                                            • {item}
                                        </Typography>
                                    ))}
                                </SectionAccordion>
                            )}

                            {strikeCard.topPlays?.length > 0 && (
                                <SectionAccordion
                                    title="Top Blink Plays"
                                    emoji="⚡"
                                    expanded={expandedPanel === "topPlays"}
                                    onChange={handleAccordionChange("topPlays")}
                                >
                                    {strikeCard.topPlays.map((item, i) => (
                                        <Typography
                                            key={i}
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                lineHeight: 1.6,
                                                mb: 0.5,
                                                pb: 0.5,
                                                borderBottom:
                                                    i < strikeCard.topPlays.length - 1
                                                        ? `1px solid ${alpha(
                                                            theme.palette.common.white,
                                                            0.06,
                                                        )}`
                                                        : "none",
                                            }}
                                        >
                                            {item}
                                        </Typography>
                                    ))}
                                </SectionAccordion>
                            )}
                        </>
                    )}
                </Box>
            </Paper>
        </Draggable>
    );
};

export default LeadContextPanel;